import { useState, useEffect, useCallback } from 'react';
import {
  listManagers,
  getManagersForRol,
  addManager,
  removeManager,
  getRolesForUsuario,
  checkManager,
  type DepartamentoManager,
  type DepartamentoRol,
} from '../api/departamentoManagers';

export function useDepartamentoManagers(rol?: DepartamentoRol) {
  const [managers, setManagers] = useState<DepartamentoManager[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = rol ? await getManagersForRol(rol) : await listManagers();
      setManagers(data);
    } catch (err: any) {
      // Si es error 403, simplemente no cargar managers (usuario no admin)
      if (err?.response?.status === 403) {
        setManagers([]);
        setError(null); // No tratar como error si es 403
      } else {
        setError(err);
        setManagers([]);
      }
    } finally {
      setLoading(false);
    }
  }, [rol]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const add = useCallback(
    async (usuarioId: number, rolToAdd: DepartamentoRol) => {
      await addManager(usuarioId, rolToAdd);
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (usuarioId: number, rolToRemove: DepartamentoRol) => {
      await removeManager(usuarioId, rolToRemove);
      await refresh();
    },
    [refresh],
  );

  return {
    managers,
    loading,
    error,
    refresh,
    add,
    remove,
  };
}

export function useUsuarioManagers(usuarioId: number | undefined) {
  const [roles, setRoles] = useState<DepartamentoRol[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!usuarioId) {
      setRoles([]);
      return;
    }
    setLoading(true);
    try {
      const data = await getRolesForUsuario(usuarioId);
      setRoles(data);
    } catch (err) {
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [usuarioId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { roles, loading, refresh };
}

export function useIsManager(usuarioId: number | undefined, rol: DepartamentoRol | undefined) {
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!usuarioId || !rol) {
      setIsManager(false);
      return;
    }

    setLoading(true);
    checkManager(usuarioId, rol)
      .then(setIsManager)
      .catch(() => setIsManager(false))
      .finally(() => setLoading(false));
  }, [usuarioId, rol]);

  return { isManager, loading };
}

